/**
 * User Service
 * 
 * Handles user management operations including incomplete user creation
 * and account completion
 * 
 * ENDPOINTS:
 * - POST /users/incomplete - Create incomplete user with Supabase account
 * - POST /supabase-auth/complete-account-setup - Complete account setup
 * - GET /supabase-auth/:id/registerUser - Get user by ID for account setup
 */

import { apiClient } from '~/lib/apiClient';
import type {
  CreateIncompleteUserRequest,
  CreateIncompleteUserResponse,
  UserWithClubs,
} from '../types';
import type { ApiSuccessResponse } from '~/lib/apiClient/types';

export const userService = {
  /**
   * Create or find incomplete user (NEW)
   * 
   * Creates an incomplete user account with Supabase auth immediately.
   * This prevents orphaned users by ensuring every local user has a Supabase account.
   * 
   * Checks for existing user (by email + tenant) and either:
   * - Creates new incomplete account if email doesn't exist (with Supabase account)
   * - Returns existing user if email already exists
   * 
   * Note: Tenant context (x-tenant-id) is handled by apiClient
   * 
   * @param data - Email, optional club ID, and source ('booking' or 'subscription')
   * @returns Full API response with success, payload, and optional message
   * 
   * @example
   * ```ts
   * const result = await userService.createOrFindIncompleteUser({
   *   email: 'user@example.com',
   *   clubId: '1',
   *   source: 'booking'
   * });
   * 
   * if (result.success && result.payload.isExisting) {
   *   // Show sign-in modal for existing user
   * } else if (result.success) {
   *   // Continue with payment method for new user
   *   // User has status: 'incomplete' and a Supabase account
   *   console.log(result.message); // "Incomplete user created successfully"
   * }
   * ```
   */
  async createOrFindIncompleteUser(
    data: CreateIncompleteUserRequest
  ): Promise<ApiSuccessResponse<CreateIncompleteUserResponse>> {
    const result = await apiClient.post<CreateIncompleteUserResponse>(
      '/users/incomplete',
      data
    );

    if (!result.success) {
      throw new Error(result.message || 'Failed to create incomplete user');
    }

    // Return the full API response (success, payload, message, meta)
    return result;
  },


  /**
   * Get user for account setup
   * 
   * Fetches user details including associated clubs for the account setup flow.
   * This is specifically used when a user clicks the setup link from their email.
   * 
   * Envelope endpoint via the gold apiClient (returns `payload`).
   *
   * @param userId - User ID from the setup email link
   * @returns User with clubs
   * @throws Error if user not found
   *
   * @example
   * ```ts
   * const user = await userService.getUserForAccountSetup('123');
   * console.log(user.email, user.clubs);
   * ```
   */
  async getUserForAccountSetup(userId: string | number): Promise<UserWithClubs> {
    const result = await apiClient.get<UserWithClubs>(
      `/supabase-auth/${userId}/registerUser`,
      { skipAuth: true }
    );

    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch user');
    }

    return result.payload;
  },
};
